import { phases, whatsappLink } from "@/mocks/llkContent";
import { ScrollReveal } from "@/components/animations/ScrollReveal";
import { StaggerContainer, StaggerItem } from "@/components/animations/StaggerContainer";
import { motion } from "motion/react";
import { useState } from "react";

export default function PhasesSection() {
  const [activePhase, setActivePhase] = useState(0);

  return (
    <section id="fases" className="relative py-20 md:py-28 bg-llk-dark">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <ScrollReveal>
            <p className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-llk-gold mb-4">
              MANIFESTO TÉCNICO
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-4">
              Soluções pensadas para cada fase da cultura
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              Cada solução entra no momento certo para proteger, estimular, nutrir ou potencializar a planta.
            </p>
          </ScrollReveal>
        </div>

        {/* Timeline / Tabs Desktop */}
        <ScrollReveal delay={0.2}>
          <div className="hidden md:block">
            <div className="flex items-center justify-between mb-10 relative">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/10 -translate-y-1/2" />
              {phases.map((phase, idx) => (
                <button
                  key={phase.phase}
                  onClick={() => setActivePhase(idx)}
                  className="relative z-10 flex flex-col items-center gap-3 transition-all duration-300 cursor-pointer group"
                >
                  <motion.div
                    animate={{
                      scale: activePhase === idx ? 1.15 : 1,
                      backgroundColor: activePhase === idx ? "#C9A84C" : "transparent",
                    }}
                    transition={{ duration: 0.3 }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      activePhase === idx
                        ? "bg-llk-gold text-llk-dark"
                        : "bg-llk-dark-2 text-white/60 border border-white/20 hover:border-llk-gold/50"
                    }`}
                  >
                    <i className={`${phase.icon} text-lg`} />
                  </motion.div>
                  <span
                    className={`text-sm font-medium transition-colors ${
                      activePhase === idx ? "text-llk-gold" : "text-white/50"
                    }`}
                  >
                    {phase.phase}
                  </span>
                </button>
              ))}
            </div>

            <motion.div
              key={activePhase}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="bg-llk-dark-2 rounded-2xl p-8 md:p-10 border border-white/10"
            >
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-display text-white font-semibold mb-4">
                    {phases[activePhase].phase}
                  </h3>
                  <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
                    {phases[activePhase].description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {phases[activePhase].products.map((p) => (
                      <span
                        key={p}
                        className="px-3 py-1.5 rounded-full bg-llk-gold/10 text-llk-gold text-sm font-medium border border-llk-gold/20"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-llk-gold text-llk-dark font-semibold rounded-full hover:bg-white transition-colors"
                  >
                    <i className="ri-whatsapp-line" />
                    Falar com Especialista
                  </a>
                </div>
                <div className="lg:w-64 flex items-center justify-center">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-llk-gold/10 flex items-center justify-center border border-llk-gold/20"
                  >
                    <i className={`${phases[activePhase].icon} text-4xl md:text-5xl text-llk-gold`} />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </ScrollReveal>

        {/* Mobile Cards */}
        <StaggerContainer className="md:hidden flex flex-col gap-4" staggerDelay={0.1} delayChildren={0.1}>
          {phases.map((phase) => (
            <StaggerItem key={phase.phase}>
              <div className="bg-llk-dark-2 rounded-xl p-5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-llk-gold/10 flex items-center justify-center">
                    <i className={`${phase.icon} text-llk-gold`} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{phase.phase}</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-3">{phase.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {phase.products.map((p) => (
                    <span
                      key={p}
                      className="px-2 py-1 rounded-full bg-llk-gold/10 text-llk-gold text-xs font-medium"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}